import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Camera, MapPin, Calendar, Clock } from "lucide-react";
import { useLocation } from "react-router-dom";
import { canClockIn } from "../../utils/attendanceLocal";
import { reverseGeocode } from "../../utils/geocode";
import {
  useGetAttendanceByDateQuery,
  useClockInOutMutation,
} from "../../slices/attendanceApiSlice";

export default function AttendancePanel() {
  const location = useLocation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [buttonState, setButtonState] = useState("clockin"); // 'clockin' | 'clockout' | 'done'
  const [locationText, setLocationText] = useState("Fetching location…");
  const [locationCoords, setLocationCoords] = useState(null);
  const [isLocationReady, setIsLocationReady] = useState(false);
  const [hasCameraPermission, setHasCameraPermission] = useState(false);
  const [hasLocationPermission, setHasLocationPermission] = useState(false);
  const [showPermDialog, setShowPermDialog] = useState(false);
  const [showClockInDialog, setShowClockInDialog] = useState(false);
  const [showClockOutDialog, setShowClockOutDialog] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [isProcessingClockOut, setIsProcessingClockOut] = useState(false);
  const [isOnLeave, setIsOnLeave] = useState(false);
  const [isOnHalfDayLeave, setIsOnHalfDayLeave] = useState(false);
  const [halfDayType, setHalfDayType] = useState(null); // "AM" or "PM"
  const [isHoliday, setIsHoliday] = useState(false);
  const [isWeekend, setIsWeekend] = useState(false);
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [lateReason, setLateReason] = useState("");
  const [pendingClockInData, setPendingClockInData] = useState(null); // Store formData temporarily

  // Check if user is CFM or ADMIN - they don't have personal attendance
  const path = location.pathname;
  const isCfmOrAdmin =
    path.startsWith("/cfm") ||
    path.startsWith("/admin") ||
    path.startsWith("/superadmin");

  // Helper functions for Asia/Thimphu timezone
  const getThimphuDateString = () => {
    const now = new Date();
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
    );
    const year = thimphuDate.getFullYear();
    const month = String(thimphuDate.getMonth() + 1).padStart(2, "0");
    const day = String(thimphuDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getThimphuTimeString = () => {
    // Return the actual current time in ISO format (UTC)
    // The backend stores this as an Instant, and the frontend will
    // convert it to the user's local timezone for display.
    return new Date().toISOString();
  };

  // Check if user is authenticated
  const userToken = useSelector((state) => state.auth?.token);
  const isUserAuthenticated = !!userToken;

  // Fetch today's attendance from API using date endpoint (Asia/Thimphu timezone)
  // Skip query if user is not authenticated
  const today = getThimphuDateString();
  const { data: todayAttendanceData, isLoading: isLoadingToday } =
    useGetAttendanceByDateQuery(today, {
      skip: !isUserAuthenticated,
    });
  const [clockInOut, { isLoading: isSubmitting }] = useClockInOutMutation();

  useEffect(() => {
    if (isLoadingToday) {
      // Keep current state while loading
      return;
    }

    // Determine button state first, then fetch location only if needed
    let needsLocation = false;
    let determinedButtonState = "clockin";
    let determinedStatusMsg = "";

    // Check today's attendance status from API
    if (!todayAttendanceData || !todayAttendanceData.attendance) {
      determinedButtonState = "clockin";
      needsLocation = true; // Need location for clock in
      setIsOnLeave(false);
      setIsOnHalfDayLeave(false);
      setHalfDayType(null);
      setIsHoliday(false);
      setIsWeekend(false);
    } else {
      // Get attendance status, type, and entries
      const attendance = todayAttendanceData.attendance;
      const attendanceStatus = attendance.attendance_status; // PRESENT, ABSENT, LEAVE, HALF_DAY_AM, HALF_DAY_PM
      const attendanceType = attendance.attendance_type; // REGULAR, HOLIDAY, WEEKEND
      const entries = Array.isArray(todayAttendanceData.entries)
        ? todayAttendanceData.entries
        : [];

      // Check if there's a clock in entry
      const hasClockIn = entries.some((e) => e.entry_type === "CLOCK_IN");
      const hasClockOut = entries.some((e) => e.entry_type === "CLOCK_OUT");

      // Check attendance type first - HOLIDAY and WEEKEND don't require clock in/out
      if (attendanceType === "HOLIDAY") {
        determinedButtonState = "done";
        determinedStatusMsg = "Today is a holiday. No clock in required.";
        needsLocation = false;
        setIsOnLeave(false);
        setIsOnHalfDayLeave(false);
        setIsHoliday(true);
        setIsWeekend(false);
      } else if (attendanceType === "WEEKEND") {
        determinedButtonState = "done";
        determinedStatusMsg = "Today is a weekend. No clock in required.";
        needsLocation = false;
        setIsOnLeave(false);
        setIsOnHalfDayLeave(false);
        setIsHoliday(false);
        setIsWeekend(true);
      } else if (attendanceStatus === "LEAVE") {
        // Full day leave
        determinedButtonState = "done";
        determinedStatusMsg = "You are on leave today. No clock in required.";
        needsLocation = false;
        setIsOnLeave(true);
        setIsOnHalfDayLeave(false);
        setIsHoliday(false);
        setIsWeekend(false);
      } else if (attendanceStatus === "HALF_DAY_AM") {
        // Half day leave - morning (AM) - can clock in/out in the afternoon
        setIsOnLeave(false);
        setIsOnHalfDayLeave(true);
        setHalfDayType("AM");
        setIsHoliday(false);
        setIsWeekend(false);
        const currentHour = new Date().getHours();
        const isMorning = currentHour < 12;

        if (isMorning) {
          // It's still morning - no clock in allowed
          determinedButtonState = "done";
          determinedStatusMsg =
            "You are on half-day leave (morning). You can clock in after 12:00 PM.";
          needsLocation = false;
        } else {
          // It's afternoon - can clock in/out
          if (!hasClockIn) {
            determinedButtonState = "clockin";
            determinedStatusMsg = "";
            needsLocation = true;
          } else if (hasClockOut) {
            determinedButtonState = "done";
            determinedStatusMsg =
              "You Clocked out for today. Come back tomorrow.";
            needsLocation = false;
          } else {
            determinedButtonState = "clockout";
            determinedStatusMsg = "";
            needsLocation = true;
          }
        }
      } else if (attendanceStatus === "HALF_DAY_PM") {
        // Half day leave - afternoon (PM) - can clock in/out in the morning
        setIsOnLeave(false);
        setIsOnHalfDayLeave(true);
        setHalfDayType("PM");
        setIsHoliday(false);
        setIsWeekend(false);
        const currentHour = new Date().getHours();
        const isMorning = currentHour < 12;

        if (isMorning) {
          // It's morning - can clock in/out
          if (!hasClockIn) {
            determinedButtonState = "clockin";
            determinedStatusMsg = "";
            needsLocation = true;
          } else if (hasClockOut) {
            determinedButtonState = "done";
            determinedStatusMsg =
              "You Clocked out for today. Come back tomorrow.";
            needsLocation = false;
          } else {
            determinedButtonState = "clockout";
            determinedStatusMsg = "";
            needsLocation = true;
          }
        } else {
          // It's afternoon - no clock in allowed
          determinedButtonState = "done";
          determinedStatusMsg =
            "You are on half-day leave (afternoon). Clock in/out is only available in the morning.";
          needsLocation = false;
        }
      } else if (
        attendanceStatus === "HALF_DAY" ||
        attendanceStatus === "HALF DAY"
      ) {
        // Legacy half day format - treat as HALF_DAY_AM for backward compatibility
        setIsOnLeave(false);
        setIsOnHalfDayLeave(true);
        setHalfDayType("AM");
        setIsHoliday(false);
        setIsWeekend(false);
        if (!hasClockIn) {
          determinedButtonState = "clockin";
          determinedStatusMsg = "";
          needsLocation = true;
        } else if (hasClockOut) {
          determinedButtonState = "done";
          determinedStatusMsg =
            "You Clocked out for today. Come back tomorrow.";
          needsLocation = false;
        } else {
          determinedButtonState = "clockout";
          determinedStatusMsg = "";
          needsLocation = true;
        }
      } else {
        // PRESENT, ABSENT, or other status - normal attendance for REGULAR days
        setIsOnLeave(false);
        setIsOnHalfDayLeave(false);
        setHalfDayType(null);
        setIsHoliday(false);
        setIsWeekend(false);
        if (!hasClockIn) {
          // No clock in entry found - user must clock in first
          determinedButtonState = "clockin";
          determinedStatusMsg = "";
          needsLocation = true;
        } else if (hasClockOut) {
          // User has both clocked in and clocked out
          determinedButtonState = "done";
          determinedStatusMsg =
            "You Clocked out for today. Come back tomorrow.";
          needsLocation = false;
        } else {
          // User has clocked in but not clocked out
          determinedButtonState = "clockout";
          determinedStatusMsg = "";
          needsLocation = true;
        }
      }
    }

    // Set button state and status message
    setButtonState(determinedButtonState);
    setStatusMsg(determinedStatusMsg);

    // Only fetch location and start camera if needed
    if (needsLocation) {
      // Start camera
      (async () => {
        try {
          const media = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: "user",
              width: { ideal: 640 },
              height: { ideal: 480 },
            },
            audio: false,
          });
          setHasCameraPermission(true);
          setStream(media);
        } catch (e) {
          setHasCameraPermission(false);
        }

        // Fetch location only if user needs to clock in or clock out
        if (navigator.geolocation) {
          const fetchLocation = (highAccuracy = true) => {
            navigator.geolocation.getCurrentPosition(
              async (pos) => {
                setHasLocationPermission(true);
                const { latitude, longitude } = pos.coords;
                const lat = Number(latitude);
                const lon = Number(longitude);
                setLocationCoords({ lat, lng: lon });
                const latFixed = lat?.toFixed(5);
                const lonFixed = lon?.toFixed(5);
                try {
                  const address = await reverseGeocode(latitude, longitude);
                  setLocationText(address);
                } catch (e) {
                  setLocationText(`${latFixed}, ${lonFixed}`);
                }
                setIsLocationReady(true);
              },
              (e) => {
                // e.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
                if (e.code === 1) {
                  setHasLocationPermission(false);
                  setLocationText("Permission denied");
                } else if (e.code === 2 && highAccuracy) {
                  // If high accuracy failed (kCLErrorLocationUnknown), try once more without it
                  console.warn(
                    "Location unavailable with high accuracy, retrying with lower accuracy...",
                  );
                  fetchLocation(false);
                  return;
                } else {
                  // Keep permission as true if it wasn't an explicit denial
                  setHasLocationPermission(e.code !== 1);
                  const errorMsg =
                    e.code === 3 ? "Location timeout" : "Location unavailable";
                  setLocationText(errorMsg);
                }
                setLocationCoords(null);
                setIsLocationReady(true);
              },
              {
                enableHighAccuracy: highAccuracy,
                maximumAge: 30000,
                timeout: highAccuracy ? 10000 : 5000,
              },
            );
          };
          fetchLocation(true);
        } else {
          setLocationText("-");
          setLocationCoords(null);
          setIsLocationReady(true); // Mark as ready even if geolocation not available
        }
      })();
    } else {
      // User doesn't need to clock in/out (on leave or already done)
      // Don't fetch location or start camera
      setLocationText("-");
      setLocationCoords(null);
      setIsLocationReady(true);
      // Stop any existing camera stream
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        setStream(null);
      }
    }

    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [todayAttendanceData, isLoadingToday]);

  // Separate effect to attach stream to video element when BOTH are ready
  useEffect(() => {
    if (stream && videoRef.current && !previewReady) {
      const v = videoRef.current;
      v.setAttribute("playsinline", "true");
      v.playsInline = true;
      v.muted = true;
      v.srcObject = stream;

      const attachVideo = async () => {
        // Wait for metadata
        await new Promise((resolve) => {
          if (v.readyState >= 2) {
            resolve();
          } else {
            v.onloadedmetadata = () => {
              resolve();
            };
            setTimeout(() => {
              resolve();
            }, 500);
          }
        });

        try {
          await v.play();
          setPreviewReady(true);
        } catch (e) {
          setPreviewReady(true); // try anyway
        }
      };

      attachVideo();
    }
  }, [stream, videoRef.current, previewReady]);

  const startCamera = async () => {
    try {
      // stop any prior stream
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setPreviewReady(false);
      // Optimized: fast constraints for quick startup
      setIsStartingCamera(true);
      const media = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 24 },
        },
        audio: false,
      });
      setStream(media);
      if (videoRef.current) {
        const v = videoRef.current;
        v.setAttribute("playsinline", "true");
        v.playsInline = true;
        v.muted = true;
        v.srcObject = media;
        // Render immediately; some browsers set dimensions slightly later
        v.onloadedmetadata = () => setPreviewReady(true);
        setTimeout(() => setPreviewReady(true), 300); // quick fallback to avoid long waits
        try {
          await v.play();
        } catch (e) {}
      }
      setIsStartingCamera(false);
    } catch (e) {
      setStatusMsg("Unable to access camera. Please allow permission.");
      setIsStartingCamera(false);
    }
  };

  const stopCamera = () => {
    try {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      if (videoRef.current) {
        try {
          videoRef.current.pause();
        } catch {}
        videoRef.current.srcObject = null;
        videoRef.current.removeAttribute("src");
        videoRef.current.load?.();
      }
    } finally {
      setStream(null);
      setPreviewReady(false);
      setIsStartingCamera(false);
    }
  };

  const drawSourceToCanvasMirrored = (source) => {
    const canvas = canvasRef.current;
    if (!canvas || !source) return "";
    const width =
      source.videoWidth ||
      source.width ||
      source.naturalWidth ||
      source.displayWidth ||
      640;
    const height =
      source.videoHeight ||
      source.height ||
      source.naturalHeight ||
      source.displayHeight ||
      480;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.save();
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(source, 0, 0, width, height);
    ctx.restore();
    return canvas.toDataURL("image/jpeg", 0.92);
  };

  // Prefer ImageCapture API for instant, non-black photos if available
  const capturePhotoFromStream = async () => {
    const currentStream = stream;
    const track = currentStream?.getVideoTracks?.()[0];
    if (track && typeof window !== "undefined" && window.ImageCapture) {
      try {
        const ic = new window.ImageCapture(track);
        const blob = await ic.takePhoto();
        if (window.createImageBitmap) {
          const bitmap = await window.createImageBitmap(blob);
          const dataUrl = drawSourceToCanvasMirrored(bitmap);
          bitmap.close?.();
          if (dataUrl) {
            return dataUrl;
          }
        } else {
          const dataUrl = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              resolve(drawSourceToCanvasMirrored(img));
              URL.revokeObjectURL(img.src);
            };
            img.onerror = () => {
              URL.revokeObjectURL(img.src);
              resolve("");
            };
            img.src = URL.createObjectURL(blob);
          });
          if (dataUrl) {
            return dataUrl;
          }
        }
      } catch {}
    }
    // Fallback to canvas capture from <video>
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return "";
    try {
      await video.play().catch(() => {});
    } catch {}
    return drawSourceToCanvasMirrored(video);
  };

  // Helper function to compress image to 2MB if larger
  const compressImage = async (dataUrl, maxSizeMB = 2) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024; // 2MB in bytes

    // Calculate actual size of base64 data (more accurate)
    const base64Data = dataUrl.split(",")[1];
    const sizeInBytes =
      (base64Data.length * 3) / 4 - (base64Data.match(/=/g) || []).length;

    // If already smaller than max size, return as-is
    if (sizeInBytes <= maxSizeBytes) {
      return dataUrl;
    }

    // Create an image element to load the data URL
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Create a canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas dimensions to match image
        let width = img.width;
        let height = img.height;
        canvas.width = width;
        canvas.height = height;

        // Draw image to canvas
        ctx.drawImage(img, 0, 0);

        // Binary search for optimal quality
        let minQuality = 0.1;
        let maxQuality = 0.9;
        let bestQuality = 0.7;
        let iterations = 0;
        const maxIterations = 15;

        while (iterations < maxIterations) {
          const quality = (minQuality + maxQuality) / 2;
          const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
          const base64Data = compressedDataUrl.split(",")[1];
          const compressedSize =
            (base64Data.length * 3) / 4 - (base64Data.match(/=/g) || []).length;

          if (compressedSize <= maxSizeBytes) {
            bestQuality = quality;
            minQuality = quality;
            // If we're within 5% of target, we're good enough
            if (compressedSize >= maxSizeBytes * 0.95) {
              resolve(compressedDataUrl);
              return;
            }
          } else {
            maxQuality = quality;
          }
          iterations++;
        }

        // Use the best quality we found
        let compressedDataUrl = canvas.toDataURL("image/jpeg", bestQuality);
        let base64Data = compressedDataUrl.split(",")[1];
        let compressedSize =
          (base64Data.length * 3) / 4 - (base64Data.match(/=/g) || []).length;

        // If still too large, reduce dimensions progressively
        if (compressedSize > maxSizeBytes) {
          let scale = 0.9;
          let attempts = 0;
          while (
            compressedSize > maxSizeBytes &&
            attempts < 10 &&
            scale > 0.3
          ) {
            width = Math.floor(img.width * scale);
            height = Math.floor(img.height * scale);
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            compressedDataUrl = canvas.toDataURL("image/jpeg", 0.7);
            base64Data = compressedDataUrl.split(",")[1];
            compressedSize =
              (base64Data.length * 3) / 4 -
              (base64Data.match(/=/g) || []).length;
            scale -= 0.1;
            attempts++;
          }
        }

        resolve(compressedDataUrl);
      };
      img.onerror = () => {
        // If compression fails, return original
        resolve(dataUrl);
      };
      img.src = dataUrl;
    });
  };

  // Helper function to convert data URL to File
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleClockIn = async () => {
    // Check if it's a holiday, weekend, or leave from API data first
    if (todayAttendanceData?.attendance) {
      const attendance = todayAttendanceData.attendance;
      const attendanceType = attendance.attendance_type;
      const attendanceStatus = attendance.attendance_status;

      if (attendanceType === "HOLIDAY") {
        setStatusMsg("Today is a holiday. No clock ins allowed.");
        return;
      }
      if (attendanceType === "WEEKEND") {
        setStatusMsg("Today is a weekend. No clock ins allowed.");
        return;
      }
      if (attendanceStatus === "LEAVE") {
        setStatusMsg("You are on leave today. No clock ins allowed.");
        return;
      }
      if (attendanceStatus === "HALF_DAY_AM") {
        const hour = new Date().getHours();
        if (hour < 12) {
          setStatusMsg(
            "You're on half-day leave (morning). You can clock in after 12:00 PM.",
          );
          return;
        }
      }
      if (attendanceStatus === "HALF_DAY_PM") {
        const hour = new Date().getHours();
        if (hour >= 12) {
          setStatusMsg(
            "You're on half-day leave (afternoon). Clock in is only available in the morning.",
          );
          return;
        }
      }
    }

    // Fallback to local storage check (for backward compatibility)
    const eligibility = canClockIn();
    if (!eligibility.allowed) {
      const reason =
        eligibility.reason === "holiday"
          ? "It's holiday today"
          : "You took the time off today";
      setStatusMsg(reason + ". No clock ins for today");
      return;
    }
    if (eligibility.reason === "half-am" || eligibility.reason === "half-pm") {
      const hour = new Date().getHours();
      const isMorning = hour < 12;
      if (
        (eligibility.reason === "half-am" && isMorning) ||
        (eligibility.reason === "half-pm" && !isMorning)
      ) {
        setStatusMsg(
          "You're on half-day leave right now. No clock-ins for this period.",
        );
        return;
      }
    }
    if (!hasCameraPermission || !hasLocationPermission || !isLocationReady) {
      setShowPermDialog(true);
      return;
    }
    if (!stream) await startCamera();
    const dataUrl = await capturePhotoFromStream();
    stopCamera();

    try {
      // Compress image if larger than 2MB
      const compressedDataUrl = await compressImage(dataUrl, 2);

      // Prepare form data
      const formData = new FormData();

      // Get date and time in Asia/Thimphu timezone
      const dateStr = getThimphuDateString(); // YYYY-MM-DD in Thimphu timezone
      const timeStr = getThimphuTimeString(); // ISO string with Thimphu time

      // Convert compressed image to File
      const imageFile = dataURLtoFile(
        compressedDataUrl,
        `clock-in-${Date.now()}.jpg`,
      );

      // Prepare location object
      const locationObj = {
        latitude: locationCoords?.lat || 0,
        longitude: locationCoords?.lng || 0,
        country: "Bhutan", // You might want to get this from geocoding
        city: locationText || "Unknown",
      };

      formData.append("date", dateStr);
      formData.append("entry_type", "CLOCK_IN");
      formData.append("time", timeStr);
      formData.append("location", JSON.stringify(locationObj));
      formData.append("image", imageFile);

      // Time-based clock-in rules (Bhutan/Asia/Thimphu time)
      const now = new Date();
      const thimphuTime = new Date(
        now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
      );
      const hours = thimphuTime.getHours();
      const minutes = thimphuTime.getMinutes();

      // Allowed: 12:00 AM (0:00) to 10:00 AM (10:00) — block at/after 10:00 AM
      const isAfterCutoff = hours >= 10;
      // Between 9:00 AM and 9:59 AM → late reason required
      const isLate = hours === 9 && !isAfterCutoff;

      if (isAfterCutoff) {
        setStatusMsg(
          "Clock-in is not allowed after 10:00 AM. Please contact your supervisor.",
        );
        return;
      }

      if (isLate) {
        // Show late reason dialog (9:00 AM – 9:59 AM)
        setPendingClockInData(formData);
        setShowLateReasonDialog(true);
        return;
      }

      // Before 9:00 AM → submit directly
      await handleClockInSubmit(formData);
    } catch (error) {
      console.error("Clock in handler error:", error);
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "Failed to clock in. Please try again.";
      setStatusMsg(errorMessage);
    }
  };

  const handleClockInSubmit = async (formData, lateReasonValue = "") => {
    try {
      // Add late_reason if provided
      if (lateReasonValue) {
        formData.append("late_reason", lateReasonValue);
      }

      // Submit to API
      await clockInOut(formData).unwrap();

      setButtonState("clockout");
      setShowClockInDialog(true);
      // auto-restart preview so the user can clock out with a live feed
      setTimeout(() => {
        startCamera();
      }, 0);
    } catch (error) {
      console.error("Clock in error:", error);
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "Failed to clock in. Please try again.";
      setStatusMsg(errorMessage);
    }
  };

  const handleClockOut = async () => {
    // Allow clock-out from 4:59 PM (16:59) up to (but not including) 12:00 AM midnight
    const nowCheck = new Date();
    const thimphuCheck = new Date(
      nowCheck.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
    );
    const coHours = thimphuCheck.getHours();
    const coMinutes = thimphuCheck.getMinutes();

    // Before 4:59 PM (i.e. before 16:59)
    const isBeforeClockOutTime =
      coHours < 16 || (coHours === 16 && coMinutes < 59);
    // At midnight or after (12:00 AM = hour 0)
    const isAfterClockOutTime = coHours === 0 && coMinutes === 0;

    if (isBeforeClockOutTime) {
      setStatusMsg(
        "Clock-out is not allowed before 4:59 PM. Please wait until then.",
      );
      return;
    }

    if (isAfterClockOutTime) {
      setStatusMsg(
        "Clock-out is not allowed at or after 12:00 AM. Please contact your supervisor.",
      );
      return;
    }

    if (!isLocationReady) {
      setStatusMsg("Please wait for location to be fetched.");
      return;
    }

    // Disable button and show processing state
    setIsProcessingClockOut(true);

    try {
      // briefly start camera to capture clock-out photo
      if (!stream) await startCamera();
      const outPhoto = await capturePhotoFromStream();
      stopCamera();

      // Compress image if larger than 2MB
      const compressedOutPhoto = await compressImage(outPhoto, 2);

      // Get current location for clock-out (fresh location, separate from clock-in)
      let clockOutLocation = "-";
      let clockOutCoords = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              maximumAge: 0,
              timeout: 5000,
            });
          });
          const { latitude, longitude } = pos.coords;
          const lat = Number(latitude);
          const lon = Number(longitude);
          clockOutCoords = { lat, lng: lon };
          try {
            clockOutLocation = await reverseGeocode(latitude, longitude);
          } catch {
            clockOutLocation = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
          }
        } catch {
          // If we can't get new location, use "-" instead of clock-in location
          clockOutLocation = "-";
          clockOutCoords = null;
        }
      }

      // Prepare form data
      const formData = new FormData();

      // Get date and time in Asia/Thimphu timezone
      const dateStr = getThimphuDateString(); // YYYY-MM-DD in Thimphu timezone
      const timeStr = getThimphuTimeString(); // ISO string with Thimphu time

      // Convert compressed image to File
      const imageFile = dataURLtoFile(
        compressedOutPhoto,
        `clock-out-${Date.now()}.jpg`,
      );

      // Prepare location object
      const locationObj = {
        latitude: clockOutCoords?.lat || 0,
        longitude: clockOutCoords?.lng || 0,
        country: "Bhutan", // You might want to get this from geocoding
        city: clockOutLocation || "Unknown",
      };

      formData.append("date", dateStr);
      formData.append("entry_type", "CLOCK_OUT");
      formData.append("time", timeStr);
      formData.append("location", JSON.stringify(locationObj));
      formData.append("image", imageFile);

      // Submit to API
      await clockInOut(formData).unwrap();

      setButtonState("done");
      setShowClockOutDialog(true);
      setStatusMsg(
        "You Clocked out for today. Thank you for your time. Come back tomorrow.",
      );
    } catch (error) {
      setStatusMsg("Failed to clock out. Please try again.");
    } finally {
      setIsProcessingClockOut(false);
    }
  };

  const renderVisual = () => {
    // ALWAYS render the video element if we have a stream so the ref exists
    if (stream) {
      return (
        <video
          ref={videoRef}
          className="w-full h-96 object-cover rounded-lg bg-black"
          style={{ transform: "scaleX(-1)" }}
          autoPlay
          muted
          playsInline
        />
      );
    }
    return (
      <div className="bg-zinc-100 rounded-lg h-96 flex items-center justify-center">
        <div className="text-center">
          <Camera className="w-16 h-16 text-zinc-400 mx-auto mb-2" />
          <p className="text-zinc-500">Camera</p>
        </div>
      </div>
    );
  };

  const renderLeaveStatus = () => {
    if (isOnLeave) {
      return (
        <div className="bg-white border-r py-6 px-8 h-full flex flex-col items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mb-4">
            <Calendar className="w-12 h-12 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-800 mb-2">On Leave</h2>
          <p className="text-zinc-600 text-center max-w-md">
            You are on leave today. No clock in required. Enjoy your time off!
          </p>
        </div>
      );
    }

    if (isHoliday) {
      return (
        <div className="bg-white border-r py-6 px-8 h-full flex flex-col items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <Calendar className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-800 mb-2">Holiday</h2>
          <p className="text-zinc-600 text-center max-w-md">
            Today is a holiday. No clock in required. Enjoy your day off!
          </p>
        </div>
      );
    }

    if (isWeekend) {
      return (
        <div className="bg-white border-r py-6 px-8 h-full flex flex-col items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center mb-4">
            <Calendar className="w-12 h-12 text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-800 mb-2">Weekend</h2>
          <p className="text-zinc-600 text-center max-w-md">
            Today is a weekend. No clock in required. Enjoy your rest!
          </p>
        </div>
      );
    }

    return null;
  };

  // If user is on full day leave, holiday, or weekend, show status instead of clock in/out
  if (isOnLeave || isHoliday || isWeekend) {
    return renderLeaveStatus();
  }

  return (
    <div className="bg-white border-r py-6 px-8 h-full">
      <h2 className="text-2xl font-bold text-zinc-800 mb-2">Clock in/out</h2>
      <p className="text-zinc-600 mb-6">
        Camera and location will be captured during clock in/out
      </p>

      {/* Show half-day leave status banner if applicable */}
      {isOnHalfDayLeave && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-yellow-800">
                Half Day Leave {halfDayType ? `(${halfDayType})` : ""}
              </h3>
              <p className="text-sm text-yellow-700">
                {halfDayType === "AM"
                  ? "You are on half-day leave (morning). You can clock in/out in the afternoon (after 12:00 PM)."
                  : halfDayType === "PM"
                    ? "You are on half-day leave (afternoon). You can clock in/out in the morning (before 12:00 PM)."
                    : "You are on half-day leave today. You can still clock in/out for the remaining half of the day."}
              </p>
            </div>
          </div>
        </div>
      )}

      {renderVisual()}
      <canvas ref={canvasRef} className="hidden" />

      <div className="mt-6">
        {buttonState === "clockin" && (
          <button
            onClick={handleClockIn}
            disabled={
              isStartingCamera ||
              (stream && !previewReady) ||
              !isLocationReady ||
              isSubmitting
            }
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors mb-4 ${
              isStartingCamera || (stream && !previewReady) || !isLocationReady
                ? "bg-zinc-300 text-white cursor-not-allowed"
                : "bg-black text-white hover:bg-zinc-800"
            }`}
          >
            {!isLocationReady
              ? "Fetching location…"
              : isStartingCamera || (stream && !previewReady)
                ? "Starting camera…"
                : "Clock in"}
          </button>
        )}
        {buttonState === "clockout" && (
          <button
            onClick={handleClockOut}
            disabled={
              isStartingCamera ||
              (stream && !previewReady) ||
              !isLocationReady ||
              isProcessingClockOut ||
              isSubmitting
            }
            className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors mb-4 ${
              isStartingCamera ||
              (stream && !previewReady) ||
              !isLocationReady ||
              isProcessingClockOut
                ? "bg-red-300 text-white cursor-not-allowed"
                : "bg-red-600 text-white hover:bg-red-700"
            }`}
          >
            {isProcessingClockOut
              ? "Processing clock out…"
              : !isLocationReady
                ? "Fetching location…"
                : isStartingCamera || (stream && !previewReady)
                  ? "Starting camera…"
                  : "Clock out"}
          </button>
        )}
        {buttonState === "done" && (
          <button
            disabled
            className="w-full bg-zinc-300 text-white py-3 px-6 rounded-lg font-semibold mb-4"
          >
            Clocked out
          </button>
        )}

        {buttonState !== "done" && (
          <div className="flex items-center space-x-2 text-zinc-600 mb-2">
            <MapPin className="w-4 h-4" />
            <span>Location: {locationText}</span>
          </div>
        )}
        {statusMsg && (
          <p className="text-center text-zinc-700 mt-4">{statusMsg}</p>
        )}
      </div>
      {showPermDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Permissions needed</h3>
            <p className="text-sm text-zinc-600 mb-4">
              This action requires camera and location permissions. Please grant
              both and try again.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowPermDialog(false)}
                className="px-4 py-2 rounded bg-zinc-900 text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {showClockInDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Clocked in</h3>
            <p className="text-sm text-zinc-600 mb-4">
              You clocked in. Good luck!
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={async () => {
                  setShowClockInDialog(false);
                  try {
                    await startCamera();
                  } catch {}
                }}
                className="px-4 py-2 rounded bg-zinc-900 text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {showClockOutDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Clocked out</h3>
            <p className="text-sm text-zinc-600 mb-4">
              You clocked out successfully. Thank you for your time!
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowClockOutDialog(false);
                }}
                className="px-4 py-2 rounded bg-zinc-900 text-white"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {showLateReasonDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Late Arrival</h3>
            <p className="text-sm text-zinc-600 mb-4">
              Please provide a reason for being late.
            </p>
            <div className="mb-4">
              <textarea
                value={lateReason}
                onChange={(e) => setLateReason(e.target.value)}
                placeholder="Enter reason for being late..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none"
                rows={4}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowLateReasonDialog(false);
                  setLateReason("");
                  setPendingClockInData(null);
                }}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!lateReason.trim()) {
                    setStatusMsg("Please provide a reason for being late.");
                    return;
                  }
                  setShowLateReasonDialog(false);
                  const reason = lateReason.trim();
                  setLateReason("");
                  if (pendingClockInData) {
                    await handleClockInSubmit(pendingClockInData, reason);
                    setPendingClockInData(null);
                  }
                }}
                className="px-4 py-2 rounded bg-zinc-900 text-white hover:bg-zinc-800"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
